import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";

// Context Providers
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { CustomerProvider } from "./context/CustomerContext";

// Layout Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";

// Customer Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import PortfolioPage from "./pages/PortfolioPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";

// Customer Auth Pages
import CustomerLoginPage from "./pages/CustomerLoginPage";
import CustomerRegisterPage from "./pages/CustomerRegisterPage";
import CustomerAccountPage from "./pages/CustomerAccountPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPortfolio from "./pages/admin/AdminPortfolio";
import AdminCustomers from "./pages/admin/AdminCustomers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Customer Layout wrapper
const CustomerLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  // Seed initial data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed-initial`);
        console.log('Initial data seeded');
      } catch (error) {
        // Data might already exist
        console.log('Data already seeded or error:', error.message);
      }
    };
    seedData();
  }, []);

  return (
    <div className="App min-h-screen bg-cream">
      <AuthProvider>
        <CustomerProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                {/* Customer Routes */}
                <Route path="/" element={<CustomerLayout><HomePage /></CustomerLayout>} />
                <Route path="/shop" element={<CustomerLayout><ShopPage /></CustomerLayout>} />
                <Route path="/shop/:categorySlug" element={<CustomerLayout><ShopPage /></CustomerLayout>} />
                <Route path="/product/:slug" element={<CustomerLayout><ProductPage /></CustomerLayout>} />
                <Route path="/cart" element={<CustomerLayout><CartPage /></CustomerLayout>} />
                <Route path="/checkout" element={<CustomerLayout><CheckoutPage /></CustomerLayout>} />
                <Route path="/order-confirmation/:orderId" element={<CustomerLayout><OrderConfirmationPage /></CustomerLayout>} />
                <Route path="/portfolio" element={<CustomerLayout><PortfolioPage /></CustomerLayout>} />
                <Route path="/about" element={<CustomerLayout><AboutPage /></CustomerLayout>} />
                <Route path="/contact" element={<CustomerLayout><ContactPage /></CustomerLayout>} />

                {/* Customer Auth Routes */}
                <Route path="/login" element={<CustomerLoginPage />} />
                <Route path="/register" element={<CustomerRegisterPage />} />
                <Route path="/account" element={<CustomerLayout><CustomerAccountPage /></CustomerLayout>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/*" element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="portfolio" element={<AdminPortfolio />} />
                </Route>
              </Routes>
            </BrowserRouter>
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))',
                },
              }}
            />
          </CartProvider>
        </CustomerProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
